import React, { useEffect, useRef, useState } from 'react'
import { ZLayers } from '~theme'
import { useConstCallback, useMinZIndex } from '~hooks'
import { Portal } from '~components/Portal'
import * as Styled from './ActionSheet.styles'
import { Icon, IconName } from '~components/Icon'

export interface Props {
  /**
   * Whether the action sheet is visible.
   * It is recommended to use this instead of conditional rendering so that animations apply.
   */
  isVisible: boolean
  /**
   * Title of the action sheet.
   */
  title?: string
  /**
   * Callback to close the action sheet.
   */
  onClose?: () => void
}

const ActionSheet: React.FC<Props> = ({ children, isVisible, title, onClose }) => {
  const touchStarted = useRef(false)
  const touchYStart = useRef(0)
  const touchYEnd = useRef(0)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const scrollerRef = useRef<HTMLDivElement | null>(null)
  const headingRef = useRef<HTMLDivElement | null>(null)
  const [isExpanded, setIsExpanded] = useState(false)

  const { zIndex, ZIndexContextProvider } = useMinZIndex(ZLayers.modal)

  const handleTouchStart = useConstCallback((e: TouchEvent) => {
    e.preventDefault()
    e.stopPropagation()
    touchYStart.current = e.touches[0].clientY
    touchStarted.current = true
  }, [])

  const handleTouchMove = useConstCallback((e: TouchEvent) => {
    if (!touchStarted.current) {
      return
    }
    e.preventDefault()
    e.stopPropagation()
    touchYEnd.current = e.touches[0].clientY
  }, [])

  const handleTouchEnd = useConstCallback(
    (e: TouchEvent) => {
      if (!touchStarted.current) {
        return
      }
      e.preventDefault()
      e.stopPropagation()
      touchStarted.current = false
      // if touch moved up more than 25% of the component, it will close/open
      if (!containerRef.current || !scrollerRef.current) {
        return
      }

      const containerSize = containerRef.current?.getBoundingClientRect().height
      const absoluteTouchDistance = touchYEnd.current - touchYStart.current
      const relativeTouchDistance = absoluteTouchDistance / containerSize

      if (relativeTouchDistance <= -0.25 || absoluteTouchDistance <= -64) {
        // check if it makes sense to expand. if we don't have scroll, there is nowhere to expand
        // idea: we don't want to confuse the user with invisible expansion of max-height,
        //       that will prevent closing the sheet on the first try.
        if (scrollerRef.current?.scrollHeight !== scrollerRef.current?.clientHeight) {
          setIsExpanded(true)
        }
      } else if (relativeTouchDistance >= 0.25 || absoluteTouchDistance >= 64) {
        if (isExpanded) {
          setIsExpanded(false)
        } else if (onClose) {
          onClose()
        }
      }
    },
    [isExpanded, onClose],
  )

  useEffect(() => {
    const heading = headingRef.current

    if (!heading) {
      return
    }

    heading.addEventListener('touchstart', handleTouchStart, { passive: false })
    window.addEventListener('touchend', handleTouchEnd, { passive: false })
    window.addEventListener('touchmove', handleTouchMove, { passive: false })

    /* it's normal to have inconsistent return in useEffect, obviously. undefined means no cleanup. */
    /* eslint-disable consistent-return */
    return () => {
      window.removeEventListener('touchmove', handleTouchMove)
      window.removeEventListener('touchend', handleTouchEnd)
      heading.removeEventListener('touchstart', handleTouchStart)
    }
  }, [headingRef.current, handleTouchStart, handleTouchMove, handleTouchEnd])

  const onOverlayClick = useConstCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()

      if (onClose) {
        onClose()
      }
    },
    [onClose],
  )

  const onContainerClick = useConstCallback((e: React.MouseEvent) => {
    e.stopPropagation()
  }, [])

  const onBack = useConstCallback(() => {
    setIsExpanded(false)
  }, [])

  return (
    <Portal rootId="action-sheet-root">
      <Styled.Overlay isVisible={isVisible} onClick={onOverlayClick} zIndex={zIndex}>
        <ZIndexContextProvider>
          <Styled.Container isExpanded={isExpanded} isVisible={isVisible} onClick={onContainerClick} ref={containerRef}>
            <Styled.Heading ref={headingRef}>
              <Styled.BackButton isVisible={isExpanded} onClick={onBack}>
                <Icon name={IconName.arrow_left} size="large" />
              </Styled.BackButton>
              <Styled.Title>{title}</Styled.Title>
            </Styled.Heading>
            <Styled.Scroller ref={scrollerRef}>{children}</Styled.Scroller>
          </Styled.Container>
        </ZIndexContextProvider>
      </Styled.Overlay>
    </Portal>
  )
}

export default ActionSheet
